/**
 * The second half of the Rubber-band send (chat-relay-reply D5;
 * docs/mockups/client-chat-relay/send-animation.html variant 5): the typed words
 * lift out of the pill and slide into the new bubble, which fades in beneath them,
 * while everything above it rides up by the bubble's height. Under
 * `prefers-reduced-motion` the quiet variant 6 runs instead: no travelling words,
 * the bubble fades in and the thread slides up gently.
 *
 * Every call cancels the previous landing first, so a second send mid-flight
 * restarts cleanly; every element is left with no inline transform at rest.
 *
 * The returned promise resolves once the words are in the bubble (the travel and its fade are
 * done, or the landing was cancelled); under reduced motion nothing travels, so it resolves at
 * once. A send that fails waits for it before showing its failure line (lib/chat/landingHold).
 *
 * Shared by the admin composer and the client's Secure room (client-chat-screen 7.2).
 */
import { animate } from 'framer-motion';

/** Where the words were when the admin let go: the pill's text box. */
export interface SendOrigin {
  rect: DOMRect;
  fontSize: number;
  lineHeight: string;
}

interface Landing {
  /** Positioned ancestor the travelling words are drawn in (the chat page). */
  stage: HTMLElement;
  /** The thread's content box, shifted up as the bubble arrives. */
  tail: HTMLElement;
  bubble: HTMLElement;
  text: string;
  origin: SendOrigin | null;
  reduced: boolean;
  /** The travelling words' color: the skin's primary text (admin by default). */
  ghostColor?: string;
}

/** Spring the mockup samples as spring(300, 28): the words and the thread move together. */
export const MOVE = { type: 'spring', stiffness: 300, damping: 28 } as const;
/** About how long that spring takes to settle; the bubble's fade-in spans it. */
const MOVE_S = 0.45;
/** The thread's gap between entries (--space-2). */
export const GAP_PX = 8;
export const QUIET_EASE = [0.16, 1, 0.3, 1] as const;

let cancelCurrent: (() => void) | null = null;

export function playLanding({
  stage, tail, bubble, text, origin, reduced, ghostColor = 'var(--color-text-primary)',
}: Landing): Promise<void> {
  cancelCurrent?.();
  const controls: Array<{ stop: () => void }> = [];
  const bubbleText = bubble.firstElementChild as HTMLElement | null;
  let ghost: HTMLElement | null = null;
  let landed: () => void = () => undefined;
  const settled = new Promise<void>((resolve) => { landed = resolve; });

  const settle = () => {
    ghost?.remove();
    ghost = null;
    if (bubbleText) bubbleText.style.color = '';
    bubble.style.opacity = '';
    landed();
  };
  cancelCurrent = () => {
    controls.forEach((control) => control.stop());
    tail.style.transform = '';
    settle();
    cancelCurrent = null;
  };

  try {
    const rise = bubble.offsetHeight + GAP_PX;
    // Where the words land: measured at the bubble's final place, before the thread is shifted down.
    const stageRect = stage.getBoundingClientRect();
    const target = bubbleText?.getBoundingClientRect() ?? null;
    // Starting values before the first paint: the animations begin on the next frame.
    tail.style.transform = `translateY(${rise}px)`;
    bubble.style.opacity = '0';
    controls.push(animate(tail, { y: [rise, 0] }, reduced ? { duration: 0.3, ease: QUIET_EASE } : MOVE));

    if (reduced || !origin || !bubbleText || !target) {
      // No words travel under reduced motion: nothing is left to land.
      if (reduced) landed();
      const fade = animate(bubble, { opacity: [0, 1] }, { duration: 0.2 });
      controls.push(fade);
      void fade.then(settle);
      return settled;
    }

    // The words leave the pill where they were and land on the bubble's text. The ghost is laid out
    // exactly as the bubble's text will be (its width, line height, weight and spacing, divided by
    // the scale it travels at), so when it fades the real text is already where the ghost ended:
    // nothing moves after it settles (Gabriel 2026-10-03).
    const dest = getComputedStyle(bubbleText);
    const scale = Number.parseFloat(dest.fontSize) / origin.fontSize || 1;
    const destLine = Number.parseFloat(dest.lineHeight);
    const node = document.createElement('div');
    node.setAttribute('aria-hidden', 'true');
    Object.assign(node.style, {
      position: 'absolute',
      left: `${origin.rect.left - stageRect.left}px`,
      top: `${origin.rect.top - stageRect.top}px`,
      width: `${target.width / scale}px`,
      fontSize: `${origin.fontSize}px`,
      lineHeight: Number.isFinite(destLine) ? `${destLine / scale}px` : origin.lineHeight,
      fontFamily: dest.fontFamily || 'var(--font-body)',
      fontWeight: dest.fontWeight,
      letterSpacing: dest.letterSpacing,
      color: ghostColor,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      transformOrigin: '0 0',
      pointerEvents: 'none',
      zIndex: '10',
    });
    node.textContent = text;
    stage.append(node);
    ghost = node;
    bubbleText.style.color = 'transparent';

    const travel = animate(node, { x: target.left - origin.rect.left, y: target.top - origin.rect.top, scale }, MOVE);
    controls.push(travel);
    controls.push(animate(bubble, { opacity: [0, 0, 1] }, { duration: MOVE_S, times: [0, 0.55, 1] }));
    void travel.then(() => {
      if (bubbleText) bubbleText.style.color = '';
      const fade = animate(node, { opacity: 0 }, { duration: 0.12 });
      controls.push(fade);
      void fade.then(settle);
    });
  } catch {
    // Motion is decoration: whatever happens, the bubble is there and readable.
    cancelCurrent?.();
  }
  return settled;
}
